/**
 * TasksPage Component
 *
 * Main entry point for the Tasks section.
 * Fetches real FHIR Task resources from Medplum. Read-only (ENG-3764 D29): no user edits a Task, so a row links to
 * the work it tracks (or opens the read-only details).
 */

import { Container, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMedplumProfile } from '@medplum/react-hooks';
import type { Task } from '@medplum/fhirtypes';
import {
  getPractitionerOrganizationRef,
  getRole,
} from '@unify/fhir-model/resources/practitioner';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TaskDrawer, TaskEmptyState, TaskFiltersPanel, TaskTableView } from './components';
import { useTaskFiltering, useTasks, useTeamMembers } from './hooks';
import type { TaskFilters as TaskFiltersType } from './types';
import { defaultTaskFilters } from './types';
import {
  buildProviderTaskNavigationUrl,
  resolveProviderTaskDestination,
} from '../../lib/providerTaskNavigation';
import { usePermissions } from '../../contexts/PermissionContext';
import { taskInboxCandidatesForViewer, taskInboxOwnerScope } from './taskInboxScope';

export function TasksPage(): JSX.Element {
  const profile = useMedplumProfile();
  const { role } = usePermissions();
  const navigate = useNavigate();

  const currentUserRef = useMemo<string | undefined>(() => {
    if (profile?.resourceType === 'Practitioner' && profile.id) {
      return `Practitioner/${profile.id}`;
    }
    return undefined;
  }, [profile]);

  // Real task data from Medplum
  const { tasks, loading, error, refresh } = useTasks();

  // Team members for assignee selectors
  const { members: teamMembers } = useTeamMembers();
  const viewerOrganizationId = useMemo(
    () => (profile?.resourceType === 'Practitioner'
      ? getPractitionerOrganizationRef(profile)?.reference?.replace(/^Organization\//, '')
      : undefined),
    [profile],
  );
  const viewerRole = useMemo(
    () => (profile?.resourceType === 'Practitioner'
      ? getRole(profile) ?? (role === 'client' ? undefined : role)
      : undefined),
    [profile, role],
  );
  const visibleMembers = useMemo(() => {
    const allowed = new Set(taskInboxCandidatesForViewer({
      viewerRole,
      viewerId: profile?.id,
      viewerOrganizationId,
      candidates: teamMembers,
    }).map((member) => member.reference));
    return teamMembers.filter((member) => allowed.has(member.reference));
  }, [profile, viewerRole, viewerOrganizationId, teamMembers]);
  const { allowedOwnerRefs, anyOrganizationOwner } = useMemo(
    () => taskInboxOwnerScope({
      viewerRole,
      viewerOrganizationId,
      practitionerRefs: visibleMembers.map((member) => member.reference),
    }),
    [viewerRole, viewerOrganizationId, visibleMembers],
  );
  const filterMembers = useMemo(
    () => visibleMembers.filter((member) => member.reference !== currentUserRef),
    [visibleMembers, currentUserRef],
  );

  // UI state
  const [filters, setFilters] = useState<TaskFiltersType>(defaultTaskFilters);

  // Read-only details for a Task with no work page to open
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  // Filter and compute task data
  const { filteredTasks, overdueCount } = useTaskFiltering(
    tasks,
    filters,
    currentUserRef,
    allowedOwnerRefs,
    anyOrganizationOwner,
  );

  const displayTasks = useMemo(() => {
    const byRef = new Map(teamMembers.map((m) => [m.reference, m.name]));
    return filteredTasks.map((t) => {
      const name = t.owner?.reference ? byRef.get(t.owner.reference) : undefined;
      if (!name) return t;
      return { ...t, owner: { ...t.owner, display: name } };
    });
  }, [filteredTasks, teamMembers]);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleNavigateToTask = useCallback(
    (task: Task): void => {
      const destination = resolveProviderTaskDestination(task);
      if (destination) {
        navigate(buildProviderTaskNavigationUrl(destination));
        return;
      }
      setSelectedTask(task);
      openModal();
    },
    [navigate, openModal],
  );

  const handleClearFilters = useCallback((): void => {
    setFilters({
      ...filters,
      provider: defaultTaskFilters.provider,
      taskType: defaultTaskFilters.taskType,
      status: [],
      assignee: 'all',
      search: '',
      showOverdueOnly: false,
    });
  }, [filters]);

  const hasFilters =
    filters.status.length > 0 ||
    filters.assignee !== 'all' ||
    filters.search !== '' ||
    filters.showOverdueOnly ||
    filters.provider.length !== defaultTaskFilters.provider.length ||
    filters.provider.some((p, i) => p !== defaultTaskFilters.provider[i]) ||
    filters.taskType.length > 0;

  const showEmptyState = !loading && !error && filteredTasks.length === 0;
  const emptyStateVariant = loading
    ? 'loading'
    : error
      ? 'error'
      : hasFilters
        ? 'no-results'
        : 'no-tasks';

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <div>
            <Title order={2}>Tasks</Title>
            <Text c="dimmed" size="sm">
              Manage your clinical and administrative tasks
            </Text>
          </div>
        </Group>

        <TaskFiltersPanel
          filters={filters}
          onFiltersChange={setFilters}
          overdueCount={overdueCount}
          teamMembers={filterMembers}
        />

        <Paper p="lg" withBorder>
          {loading || error || showEmptyState ? (
            <TaskEmptyState
              variant={emptyStateVariant}
              hasFilters={hasFilters}
              onClearFilters={handleClearFilters}
              onRetry={refresh}
              errorMessage={error ?? undefined}
            />
          ) : (
            <TaskTableView
              tasks={displayTasks}
              onTaskClick={handleNavigateToTask}
              sortBy={filters.sortBy}
              sortDirection={filters.sortDirection}
              showAssignee
              onSort={(column) => {
                setFilters((prev) =>
                  prev.sortBy === column
                    ? { ...prev, sortDirection: prev.sortDirection === 'asc' ? 'desc' : 'asc' }
                    : { ...prev, sortBy: column, sortDirection: 'asc' },
                );
              }}
            />
          )}
        </Paper>

        {/* Read-only Task details */}
        <TaskDrawer opened={modalOpened} onClose={closeModal} task={selectedTask} />
      </Stack>
    </Container>
  );
}

